import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Shield, Database, Eye, Lock, UserCheck, Mail, Phone, MapPin } from "lucide-react";

const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* En-tête */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-12 w-12 text-green-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Politique de Confidentialité</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comment nous collectons, utilisons et protégeons vos données personnelles
          </p>
        </div>

        <div className="space-y-8">
          {/* 1. Responsable du traitement */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <UserCheck className="h-6 w-6 mr-3" />
                1. Responsable du traitement des données
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg text-green-700">Balinaisa</h3>
                  <div className="space-y-2 text-gray-700">
                    <p><span className="font-medium">Forme juridique :</span> SAS</p>
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 mr-2 mt-1 text-green-600" />
                      <div>
                        <span className="font-medium">Siège social :</span><br />
                        13 rue Anders Celsius<br />
                        33470 Le Teich, France
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-green-600" />
                      <span><span className="font-medium">Téléphone :</span> 07 45 06 52 16</span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-green-600" />
                      <span><span className="font-medium">Email :</span> contact@balinaisa.com</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-800">Informations légales</h4>
                  <div className="space-y-2 text-gray-700">
                    <p><span className="font-medium">SIRET :</span> 948 801 808 00015</p>
                    <p><span className="font-medium">RCS :</span> Bordeaux B 948 801 808</p>
                    <p><span className="font-medium">TVA intracommunautaire :</span> FR 91 948801808</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 2. Données collectées */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Database className="h-6 w-6 mr-3" />
                2. Données personnelles collectées
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-blue-700 mb-3">Types de données collectées</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2"></div>
                      <div>
                        <span className="font-medium">Données d'identité :</span> nom, prénom, adresse email
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2"></div>
                      <div>
                        <span className="font-medium">Données de contact :</span> numéro de téléphone, adresse postale
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2"></div>
                      <div>
                        <span className="font-medium">Données de navigation :</span> adresse IP, cookies, pages visitées
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2"></div>
                      <div>
                        <span className="font-medium">Données de communication :</span> messages via formulaires de contact
                      </div>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-700 mb-3">Moyens de collecte</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      Formulaires de contact
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      Inscription à la newsletter
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      Navigation sur le site (cookies)
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      Échanges téléphoniques ou par email
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3. Finalités et bases légales */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Eye className="h-6 w-6 mr-3" />
                3. Finalités du traitement et bases légales
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                  <h4 className="font-semibold text-purple-800 mb-2">Gestion des demandes de contact</h4>
                  <p className="text-purple-700 mb-2">
                    <span className="font-medium">Finalité :</span> Répondre à vos demandes d'information et assurer le suivi de la relation client.
                  </p>
                  <p className="text-purple-700">
                    <span className="font-medium">Base légale :</span> Intérêt légitime de l'entreprise
                  </p>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                  <h4 className="font-semibold text-purple-800 mb-2">Communication commerciale</h4>
                  <p className="text-purple-700 mb-2">
                    <span className="font-medium">Finalité :</span> Envoi d'informations sur nos services et offres commerciales.
                  </p>
                  <p className="text-purple-700">
                    <span className="font-medium">Base légale :</span> Consentement explicite
                  </p>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                  <h4 className="font-semibold text-purple-800 mb-2">Analyse statistique</h4>
                  <p className="text-purple-700 mb-2">
                    <span className="font-medium">Finalité :</span> Amélioration de l'expérience utilisateur et optimisation du site web.
                  </p>
                  <p className="text-purple-700">
                    <span className="font-medium">Base légale :</span> Intérêt légitime de l'entreprise
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 4. Durée de conservation */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Database className="h-6 w-6 mr-3" />
                4. Durée de conservation des données
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-orange-700 mb-3">Données de contact</h4>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-orange-800">
                      <span className="font-semibold">3 ans</span> maximum après le dernier contact ou la fin de la relation commerciale.
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-orange-700 mb-3">Données de navigation</h4>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-orange-800">
                      <span className="font-semibold">13 mois</span> maximum pour les cookies de mesure d'audience.
                    </p>
                  </div>
                </div>
              </div>
              <Separator />
              <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                <p className="text-orange-800">
                  <span className="font-semibold">Note :</span> Les données peuvent être conservées plus longtemps si une obligation légale l'exige 
                  ou en cas de contentieux jusqu'à la résolution de celui-ci.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 5. Vos droits RGPD */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Lock className="h-6 w-6 mr-3" />
                5. Vos droits sur vos données personnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <p className="text-gray-700 leading-relaxed">
                Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, 
                vous disposez des droits suivants :
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-indigo-700">Droit d'accès :</span>
                      <p className="text-gray-600 text-sm">Obtenir une copie de vos données personnelles</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-indigo-700">Droit de rectification :</span>
                      <p className="text-gray-600 text-sm">Corriger des données inexactes ou incomplètes</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-indigo-700">Droit à l'effacement :</span>
                      <p className="text-gray-600 text-sm">Demander la suppression de vos données</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-indigo-700">Droit d'opposition :</span>
                      <p className="text-gray-600 text-sm">Vous opposer au traitement de vos données</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-indigo-700">Droit à la portabilité :</span>
                      <p className="text-gray-600 text-sm">Récupérer vos données dans un format structuré</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3 mt-1"></div>
                    <div>
                      <span className="font-semibold text-indigo-700">Droit à la limitation :</span>
                      <p className="text-gray-600 text-sm">Limiter le traitement de vos données</p>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="bg-indigo-50 border border-indigo-200 p-6 rounded-lg">
                <h4 className="font-semibold text-indigo-800 mb-3">Comment exercer vos droits ?</h4>
                <div className="space-y-3">
                  <div className="flex items-center text-indigo-800">
                    <Mail className="h-5 w-5 mr-3" />
                    <span><span className="font-medium">Par email :</span> contact@balinaisa.com</span>
                  </div>
                  <div className="flex items-center text-indigo-800">
                    <Phone className="h-5 w-5 mr-3" />
                    <span><span className="font-medium">Par téléphone :</span> 07 45 06 52 16</span>
                  </div>
                  <div className="flex items-start text-indigo-800">
                    <MapPin className="h-5 w-5 mr-3 mt-1" />
                    <div>
                      <span className="font-medium">Par courrier :</span><br />
                      Balinaisa<br />
                      13 rue Anders Celsius<br />
                      33470 Le Teich, France
                    </div>
                  </div>
                </div>
                <p className="text-indigo-700 mt-4 text-sm">
                  <span className="font-medium">Important :</span> Pour des raisons de sécurité, nous pourrons vous demander 
                  de justifier de votre identité avant de traiter votre demande.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 6. Sécurité des données */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Shield className="h-6 w-6 mr-3" />
                6. Sécurité et protection des données
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Balinaisa met en œuvre toutes les mesures techniques et organisationnelles appropriées 
                pour protéger vos données personnelles contre :
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
                  La destruction accidentelle ou illicite
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
                  La perte accidentelle
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
                  L'altération, la diffusion ou l'accès non autorisé
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
                  Toute autre forme de traitement illicite
                </li>
              </ul>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                <p className="text-red-800">
                  <span className="font-semibold">Engagement :</span> Nous nous engageons à vous informer dans les plus brefs délais 
                  en cas de violation de données susceptible d'engendrer un risque élevé pour vos droits et libertés.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-500">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;